import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../api/axiosConfig';
import { getMediaBaseUrl } from '../utils/mediaUrl';

// MUI
import {
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  Avatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  TextField,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { deepOrange } from '@mui/material/colors';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';

// React DnD
import {
  DndProvider,
  useDrag,
  useDrop,
  useDragLayer
} from 'react-dnd';
import {
  /** on importe désormais MultiBackend en “named export” */
  MultiBackend,
  MouseTransition,
  TouchTransition
} from 'react-dnd-multi-backend';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';

/**
 * Définition “manuelle” du preset HTML5 + Tactile
 * (puisqu’il n’y a plus de “HTML5toTouch” exporté par défaut)
 */
const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      preview: true,
      transition: TouchTransition,
    },
  ],
};

const mediaUrl = getMediaBaseUrl();

// Pour React-DnD
const ItemTypes = { PLAYER: 'PLAYER' };

// Fonction utilitaire pour construire l'URL de l'avatar du joueur
const getPlayerAvatarUrl = (playerId) => {
  return playerId
    ? `${mediaUrl}/photos/${playerId}.png?${new Date().getTime()}`
    : '/path/to/default-player-avatar.png';
};

// -----------------------------------------------------------------------------
// 1) Capa de Drag personalizada (el avatar que sigue el ratón durante el drag)
// -----------------------------------------------------------------------------
function CustomDragLayer() {
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getSourceClientOffset(),
  }));

  if (!isDragging || !currentOffset || !item) return null;

  const style = {
    position: 'fixed',
    pointerEvents: 'none',
    left: currentOffset.x,
    top: currentOffset.y,
    transform: 'translate(-50%, -50%)',
    zIndex: 100,
  };

  return (
    <Box style={style}>
      <Avatar
        sx={{ width: 80, height: 80 }}
        src={item.player.avatarUrl}
      >
        {item.player.nom?.charAt(0).toUpperCase() || '?'}
      </Avatar>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// 2) Elemento draggable en la lista de la izquierda (banco)
//    => se puede arrastrar para activar al jugador o hacer un swap
// -----------------------------------------------------------------------------
function PlayerListItem({ player, onSwap }) {
  // Drag desde la izquierda hacia la derecha
  const [{ isDragging }, dragRef] = useDrag({
    type: ItemTypes.PLAYER,
    item: { player, source: 'left' },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  // Drop desde la derecha => sustitución
  const [{ isOver, canDrop }, dropRef] = useDrop({
    accept: ItemTypes.PLAYER,
    canDrop: (dragItem) => dragItem.source === 'right',
    drop: (dragItem) => onSwap(dragItem.player, player),
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  return (
    <ListItem
      ref={(node) => {
        dragRef(node);
        dropRef(node);
      }}
      sx={{
        alignItems: 'center',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        backgroundColor: isOver && canDrop ? '#ddd' : 'transparent',
      }}
    >
      <img
        src={player.avatarUrl}
        alt={`${player.nom} avatar`}
        style={{
          borderRadius: '50%',
          width: 40,
          height: 40,
          marginRight: 8
        }}
      />
      {player.nom}
    </ListItem>
  );
}

// -----------------------------------------------------------------------------
// 3) DropZoneBanc : la zona "banco" a la izquierda. 
//    Si se suelta un jugador activo encima => sale a la banca
// -----------------------------------------------------------------------------
function DropZoneBanc({ onPlayerCheckOut, isRunning, children, t }) {
  const [{ isOver }, dropRef] = useDrop({
    accept: ItemTypes.PLAYER,
    canDrop: (item) => item.source === 'right',
    drop: (item) => {
      // Podemos impedir o alertar si el cronómetro está en marcha
      if (isRunning) {
        alert(t('estadisticas.stopTimerWarning'));
        return;
      }
      onPlayerCheckOut(item.player);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  return (
    <Box
      ref={dropRef}
      sx={{
        position: 'relative',
        width: 280,
        borderRight: '1px solid #ccc',
        p: 2,
        overflowY: 'auto',
      }}
    >
      {children}
      {isOver && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(0,0,0,0.2)',
            pointerEvents: 'none'
          }}
        />
      )}
    </Box>
  );
}

// -----------------------------------------------------------------------------
// 4) DroppableCourt : el “campo” central (si se suelta un jugador => abre el dialogo de tiro)
// -----------------------------------------------------------------------------
function DroppableCourt({ onPlayerDrop, children }) {
  const [{ isOver }, dropRef] = useDrop({
    accept: ItemTypes.PLAYER,
    drop: (item) => onPlayerDrop(item.player),
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  });

  return (
    <Box
      ref={dropRef}
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: isOver ? '#ddd' : 'transparent',
      }}
    >
      {children}
    </Box>
  );
}

// -----------------------------------------------------------------------------
// 5) ActiveDropZone : zona droppable a la derecha (jugadores activos)
// -----------------------------------------------------------------------------
function ActiveDropZone({
  activePlayers,
  setActivePlayers,
  setPlayers,
  onPlayerCheckIn,
  isRunning,
  children,
  t
}) {
  const [{ isOver }, dropRef] = useDrop({
    accept: ItemTypes.PLAYER,
    canDrop: (item) => item.source === 'left',
    drop: (item) => {
      if (isRunning) {
        alert(t('estadisticas.stopTimerWarning'));
        return;
      }
      // Si ya está activo, no hacemos nada
      const alreadyActive = activePlayers.some((p) => p.id === item.player.id);
      if (alreadyActive) return;

      // Máximo 5 jugadores activos
      if (activePlayers.length >= 5) {
        alert(t('estadisticas.maxActivePlayers'));
        return;
      }

      // Sacar de la lista izquierda
      setPlayers((prev) => prev.filter((p) => p.id !== item.player.id));
      // Entrar a la derecha
      setActivePlayers((prev) => [...prev, item.player]);

      onPlayerCheckIn(item.player.id);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  });

  return (
    <Box
      ref={dropRef}
      sx={{
        position: 'relative',
        flex: '0 0 12%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        backgroundColor: '#f0f0f0',
        overflowY: 'auto',
        paddingTop: 2
      }}
    >
      {children}
      {isOver && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            bgcolor: 'rgba(0,0,0,0.3)',
            color: 'white',
            textAlign: 'center',
            p: 0.5,
          }}
        >
          {t('estadisticas.dropToActivate')}
        </Box>
      )}
    </Box>
  );
}

// -----------------------------------------------------------------------------
// 6) ActivePlayerAvatar : avatar de un jugador activo en la zona derecha
// -----------------------------------------------------------------------------
function ActivePlayerAvatar({ player, onDoubleClick }) {
  // Drag desde la derecha
  const [{ isDragging }, dragRef] = useDrag({
    type: ItemTypes.PLAYER,
    item: { player, source: 'right' },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  return (
    <Box
      ref={dragRef}
      sx={{
        position: 'relative',
        mb: 2,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'grab',
        textAlign: 'center'
      }}
      onDoubleClick={() => onDoubleClick(player)}
    >
      <Avatar
        sx={{
          bgcolor: deepOrange[500],
          width: 80,
          height: 80,
          margin: '0 auto',
        }}
        src={player.avatarUrl}
      >
        {player.nom?.charAt(0).toUpperCase() || '?'}
      </Avatar>
      <Box
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          bgcolor: 'white',
          borderRadius: '50%',
          p: '2px',
        }}
      >
        <SportsBasketballIcon fontSize="small" color="primary" />
      </Box>
      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
        {player.nom}
      </Typography>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// 7) BasketballCourt : el campo + la zona derecha para jugadores activos
// -----------------------------------------------------------------------------
function BasketballCourt({
  activePlayers,
  setActivePlayers,
  onPlayerDropForShot,
  onOpenSpecialDialog,
  setPlayers,
  onPlayerCheckIn,
  isRunning,
  t
}) {
  return (
    <Box sx={{ display: 'flex', flex: 1, flexDirection: 'row' }}>
      {/* Parte central (campo) */}
      <Box
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          backgroundColor: '#eaeaea',
          paddingTop: 2
        }}
      >
        <Paper
          elevation={3}
          sx={{
            position: 'relative',
            width: '95%',
            aspectRatio: '94/50', // 94 x 50 ft
            maxWidth: '100%',
            overflow: 'hidden'
          }}
        >
          <DroppableCourt onPlayerDrop={onPlayerDropForShot}>
            {/* Campo estilizado en SVG */}
            <svg width="100%" height="100%" viewBox="0 0 940 500">
              <rect x="0" y="0" width="940" height="500" fill="#E65D1E" />
              <rect
                x="50" y="50"
                width="840" height="400"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
              <line
                x1="470" y1="50"
                x2="470" y2="450"
                stroke="#FFFFFF" strokeWidth="3"
              />
              <circle
                cx="470" cy="250" r="80"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
              {/* Zona izquierda */}
              <rect
                x="50" y="150"
                width="140" height="200"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
              <line
                x1="190" y1="150"
                x2="190" y2="350"
                stroke="#FFFFFF" strokeWidth="3"
              />
              <path
                d="M 190 190 A 60 60 0 0 1 190 310"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
              {/* Zona derecha */}
              <rect
                x="750" y="150"
                width="140" height="200"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
              <line
                x1="750" y1="150"
                x2="750" y2="350"
                stroke="#FFFFFF" strokeWidth="3"
              />
              <path
                d="M 750 190 A 60 60 0 0 0 750 310"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
              {/* Aro izquierdo */}
              <circle cx="55" cy="250" r="7" fill="none" stroke="#FFFFFF" strokeWidth="3" />
              <line x1="60" y1="230" x2="60" y2="270" stroke="#FFFFFF" strokeWidth="4" />
              {/* Aro derecho */}
              <circle cx="885" cy="250" r="7" fill="none" stroke="#FFFFFF" strokeWidth="3" />
              <line x1="880" y1="230" x2="880" y2="270" stroke="#FFFFFF" strokeWidth="4" />

              {/* Arcos de la esquina */}
              <path
                d="M 50 50 A 210 180 0 1 1 50 450"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
              <path
                d="M 890 50 A 210 180 0 1 0 890 450"
                fill="none" stroke="#FFFFFF" strokeWidth="3"
              />
            </svg>
          </DroppableCourt>
        </Paper>
      </Box>

      {/* Columna derecha => jugadores activos */}
      <ActiveDropZone
        activePlayers={activePlayers}
        setActivePlayers={setActivePlayers}
        setPlayers={setPlayers}
        onPlayerCheckIn={onPlayerCheckIn}
        isRunning={isRunning}
        t={t}
      >
        {activePlayers.map((p) => (
          <ActivePlayerAvatar
            key={p.id}
            player={p}
            onDoubleClick={onOpenSpecialDialog}
          />
        ))}
      </ActiveDropZone>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// 8) Componente principal : Estadisticas
// -----------------------------------------------------------------------------
export default function Estadisticas({ groupeId, groupName }) {
  const { t } = useTranslation();
  // 8.1) Estado: jugadores inactivos (banco)
  const [players, setPlayers] = useState([]);
  // 8.2) Estado: jugadores activos (campo, columna derecha)
  const [activePlayers, setActivePlayers] = useState([]);

  // 8.3) Historial de acciones (tiros, faltas, etc.)
  const [actionsLog, setActionsLog] = useState([]);

  // 8.4) Diálogos
  const [openShotDialog, setOpenShotDialog] = useState(false);
  const [shotPlayer, setShotPlayer] = useState(null);
  const [openSpecialDialog, setOpenSpecialDialog] = useState(false);
  const [specialPlayer, setSpecialPlayer] = useState(null);
  const [openConfigGameDialog, setOpenConfigGameDialog] = useState(false);

  // 8.5) Parámetros de partido
  const [unpublishedGame, setUnpublishedGame] = useState({
    game_label: '',
    adversary_name: '',
    number_of_periods: 4,
    period_duration: 10,
    game_date: '',
    game_location: 'domicile'
  });
  const [currentGame, setCurrentGame] = useState(null);

  // 8.6) Marcador
  const [puntuacionLocal, setPuntuacionLocal] = useState(0);
  const [puntuacionVisitante, setPuntuacionVisitante] = useState(0);

  // 8.7) Período actual
  const [currentPeriod, setCurrentPeriod] = useState(1);

  // 8.8) Cronómetro del partido
  const [timeLeft, setTimeLeft] = useState(0);
  const [periodSeconds, setPeriodSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef(null);

  // 8.9) Crono de vídeo (invisible)
  const [videoSeconds, setVideoSeconds] = useState(0);
  const [inPeriod, setInPeriod] = useState(false);
  const videoTimerRef = useRef(null);

  // 8.10) Tracking de tiempos de juego por jugador
  /**
   * playerTimeTracking = {
   *   [playerId]: {
   *     realTimeSegments: [ { in, out, periodIn, periodOut }, ... ],
   *     videoTimeSegments: [ { in, out, periodIn, periodOut }, ... ],
   *     currentlyOnFloor: bool,
   *     realTimeIn: number | null,
   *     videoTimeIn: number | null,
   *     periodIn: number | null
   *   }
   * }
   */
  const [playerTimeTracking, setPlayerTimeTracking] = useState({});

  // 8.11) Snackbar (mensaje de cambio)
  const [exchangeMessage, setExchangeMessage] = useState(null);

  // 8.12) Gestión del "long click" en el marcador visitante
  const [mouseDownTimer, setMouseDownTimer] = useState(null);

  // ---------------------------------------------------------------------------
  // useEffect: carga inicial de la lista de jugadores (banco)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    api
      .get(`/api/cantera/${groupeId}`)
      .then((response) => {
        if (response.data && response.data.players) {
          const fetchedPlayers = response.data.players.map((p) => ({
            ...p,
            avatarUrl: getPlayerAvatarUrl(p.id),
          }));
          setPlayers(fetchedPlayers);
        }
      })
      .catch((error) => {
        console.error('Error al obtener los jugadores:', error);
      });
  }, [groupeId]);

  // ---------------------------------------------------------------------------
  // Cronómetro del partido => start/stop
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) return 0;
          return prev - 1;
        });
        setPeriodSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRunning]);

  // ---------------------------------------------------------------------------
  // Crono de vídeo => corre si inPeriod = true
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (inPeriod) {
      videoTimerRef.current = setInterval(() => {
        setVideoSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(videoTimerRef.current);
    }
    return () => clearInterval(videoTimerRef.current);
  }, [inPeriod]);

  // ---------------------------------------------------------------------------
  // Formatear el tiempo en mm:ss
  // ---------------------------------------------------------------------------
  const formatTime = (totalSeconds) => {
    const min = Math.floor(totalSeconds / 60);
    const sec = totalSeconds % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  // ---------------------------------------------------------------------------
  // Configurar el partido => abre/cierra el diálogo
  // ---------------------------------------------------------------------------
  const handleOpenConfigGame = () => {
    setOpenConfigGameDialog(true);
  };
  const handleCloseConfigGame = () => {
    setOpenConfigGameDialog(false);
  };

  const handleConfigGame = () => {
    setCurrentGame({ ...unpublishedGame });
    setOpenConfigGameDialog(false);

    // Reinicio de marcador, crono, acciones, etc.
    setPuntuacionLocal(0);
    setPuntuacionVisitante(0);
    setCurrentPeriod(1);
    setTimeLeft(unpublishedGame.period_duration * 60);
    setPeriodSeconds(0);
    setActionsLog([]);
    setIsRunning(false);

    setVideoSeconds(0);
    setInPeriod(false);

    setPlayerTimeTracking({});
  };

  // ---------------------------------------------------------------------------
  // Fin de período
  // ---------------------------------------------------------------------------
  const handleEndOfPeriod = () => {
    if (!currentGame) return;

    setIsRunning(false);
    setInPeriod(false);
    setVideoSeconds(0);

    if (currentPeriod < currentGame.number_of_periods) {
      setCurrentPeriod((prev) => prev + 1);
      setTimeLeft(currentGame.period_duration * 60);
      setPeriodSeconds(0);
    } else {
      alert(t('estadisticas.endOfGame'));
    }
  };

  // ---------------------------------------------------------------------------
  // Start / Stop crono
  // ---------------------------------------------------------------------------
  const handleStartStopTimer = () => {
    setIsRunning((prev) => {
      const nextVal = !prev;
      if (nextVal && !inPeriod) {
        // Arrancamos el período
        setInPeriod(true);
        setVideoSeconds(0);
      }
      return nextVal;
    });
  };

  // ---------------------------------------------------------------------------
  // Gestión du Swap (drag desde la derecha => izquierda, y la “target” entra)
  // ---------------------------------------------------------------------------
  const handleSwap = (draggedPlayer, targetPlayer) => {
    if (isRunning) {
      alert(t('estadisticas.stopTimerWarning'));
      return;
    }
    // Quitar el jugador arrastrado (dragged) de los activos
    setActivePlayers((prev) => prev.filter((p) => p.id !== draggedPlayer.id));
    handlePlayerCheckOut(draggedPlayer.id);

    // Mandarlo al banco (donde estaba targetPlayer)
    setPlayers((prev) => {
      const tIndex = prev.findIndex((p) => p.id === targetPlayer.id);
      if (tIndex === -1) return prev;
      const newList = prev.filter((p) => p.id !== targetPlayer.id);
      // Insertamos draggedPlayer en la posición de target
      newList.splice(tIndex, 0, draggedPlayer);
      return newList;
    });

    // El jugador target entra al campo
    setActivePlayers((prev) => [...prev, targetPlayer]);
    handlePlayerCheckIn(targetPlayer.id);

    showExchangeMessage(targetPlayer, draggedPlayer);
  };

  const showExchangeMessage = (inPlayer, outPlayer) => {
    const msg = t('estadisticas.substitution', { inPlayer: inPlayer.nom, outPlayer: outPlayer.nom });
    setExchangeMessage(msg);
    setTimeout(() => setExchangeMessage(null), 3000);
  };

  // ---------------------------------------------------------------------------
  // Salir un jugador activo sin sustitución directa
  // ---------------------------------------------------------------------------
  const handlePlayerCheckOutZone = (player) => {
    setActivePlayers((prev) => prev.filter((p) => p.id !== player.id));
    handlePlayerCheckOut(player.id);
    setPlayers((prev) => [...prev, player]);
  };

  // ---------------------------------------------------------------------------
  // Tracking tiempos => entrada
  // ---------------------------------------------------------------------------
  const handlePlayerCheckIn = (playerId) => {
    // Buscamos el objeto del jugador para mostrar su nombre en consola
    const allPlayersList = [...players, ...activePlayers];
    const foundPlayer = allPlayersList.find((p) => p.id === playerId);

    if (foundPlayer) {
      console.log(
        `=== [IN] ${foundPlayer.nom} => Crono: ${formatTime(timeLeft)} (Período: ${currentPeriod})`
      );
    }

    setPlayerTimeTracking((prev) => {
      const data = prev[playerId] || {
        realTimeSegments: [],
        videoTimeSegments: [],
        currentlyOnFloor: false,
        realTimeIn: null,
        videoTimeIn: null,
        periodIn: null
      };
      return {
        ...prev,
        [playerId]: {
          ...data,
          currentlyOnFloor: true,
          realTimeIn: periodSeconds,
          videoTimeIn: videoSeconds,
          periodIn: currentPeriod
        }
      };
    });
  };

  // ---------------------------------------------------------------------------
  // Tracking tiempos => salida
  // ---------------------------------------------------------------------------
  const handlePlayerCheckOut = (playerId) => {
    const allPlayersList = [...players, ...activePlayers];
    const foundPlayer = allPlayersList.find((p) => p.id === playerId);

    setPlayerTimeTracking((prev) => {
      const pData = prev[playerId];
      if (!pData || !pData.currentlyOnFloor) {
        // Nada que cerrar
        return prev;
      }

      const newRealSegment = {
        in: pData.realTimeIn,
        out: periodSeconds,
        periodIn: pData.periodIn,
        periodOut: currentPeriod
      };
      const newVideoSegment = {
        in: pData.videoTimeIn,
        out: videoSeconds,
        periodIn: pData.periodIn,
        periodOut: currentPeriod
      };

      if (foundPlayer) {
        console.log(
          `=== [OUT] ${foundPlayer.nom} => Crono: ${formatTime(timeLeft)} (Período: ${currentPeriod})`
        );
      }

      return {
        ...prev,
        [playerId]: {
          ...pData,
          currentlyOnFloor: false,
          realTimeSegments: [...pData.realTimeSegments, newRealSegment],
          videoTimeSegments: [...pData.videoTimeSegments, newVideoSegment],
          realTimeIn: null,
          videoTimeIn: null,
          periodIn: null
        }
      };
    });
  };

  // ---------------------------------------------------------------------------
  // Drop en el campo => abre el diálogo de tiro
  // ---------------------------------------------------------------------------
  const handlePlayerDropForShot = (player) => {
    setShotPlayer(player);
    setOpenShotDialog(true);
  };

  // ---------------------------------------------------------------------------
  // Tiro con corrección de ~10 segundos
  // ---------------------------------------------------------------------------
  const handleShot = (type, result) => {
    if (!shotPlayer) return;

    if (result === 'anotado') {
      let pts = 0;
      if (type === '2pts') pts = 2;
      if (type === '3pts') pts = 3;
      if (type === 'TL')   pts = 1;
      setPuntuacionLocal((prev) => prev + pts);
    }

    let actionLabel = '';
    if (type === '2pts' && result === 'anotado') actionLabel = '2pts anotado';
    else if (type === '2pts' && result === 'fallado') actionLabel = '2pts fallado';
    else if (type === '3pts' && result === 'anotado') actionLabel = '3pts anotado';
    else if (type === '3pts' && result === 'fallado') actionLabel = '3pts fallado';
    else if (type === 'TL'   && result === 'anotado') actionLabel = 'Tiro libre anotado';
    else if (type === 'TL'   && result === 'fallado') actionLabel = 'Tiro libre fallado';

    // Restar ~10s al cronómetro del período
    const correctedTime = Math.max(0, periodSeconds - 10);

    setActionsLog((prev) => [
      ...prev,
      {
        playerId: shotPlayer.id,
        action: actionLabel,
        period: currentPeriod,
        timeSec: correctedTime
      }
    ]);

    setOpenShotDialog(false);
    setShotPlayer(null);
  };

  const handleCloseShotDialog = () => {
    setOpenShotDialog(false);
    setShotPlayer(null);
  };

  // ---------------------------------------------------------------------------
  // Acciones especiales: doble clic en jugador activo (rebotes, faltas, etc.)
  // ---------------------------------------------------------------------------
  const openDialogSpecialActions = (player) => {
    setSpecialPlayer(player);
    setOpenSpecialDialog(true);
  };

  const handleCloseSpecialDialog = () => {
    setOpenSpecialDialog(false);
    setSpecialPlayer(null);
  };

  // ---------------------------------------------------------------------------
  // Acción especial (rebote, falta, etc.) con corrección de ~10s
  // ---------------------------------------------------------------------------
  const handleSpecialAction = (action) => {
    if (!specialPlayer) return;

    const correctedTime = Math.max(0, periodSeconds - 10);

    setActionsLog((prev) => [
      ...prev,
      {
        playerId: specialPlayer.id,
        action,
        period: currentPeriod,
        timeSec: correctedTime
      }
    ]);
    setOpenSpecialDialog(false);
    setSpecialPlayer(null);
  };

  // ---------------------------------------------------------------------------
  // Marcador visitante: clic / long press
  // ---------------------------------------------------------------------------
  const handleAdversaryScoreMouseDown = (points) => {
    const timer = setTimeout(() => {
      setPuntuacionVisitante((prev) => Math.max(0, prev - points));
      setMouseDownTimer(null);
    }, 1000);
    setMouseDownTimer(timer);
  };

  const handleAdversaryScoreMouseUp = (points) => {
    if (mouseDownTimer) {
      clearTimeout(mouseDownTimer);
      setMouseDownTimer(null);
      setPuntuacionVisitante((prev) => prev + points);
    }
  };

  // ---------------------------------------------------------------------------
  // Guardar => 1 inserción de game + 1 POST con las acciones
  // ---------------------------------------------------------------------------
  const handleSaveGameAndActions = () => {
    if (!currentGame) {
      alert(t('estadisticas.noGameConfigured'));
      return;
    }

    // Forzamos la salida de todos los jugadores activos
    activePlayers.forEach((p) => {
      handlePlayerCheckOut(p.id);
    });

    // Si no hay acciones, pedimos confirmación
    if (actionsLog.length === 0) {
      if (!window.confirm(t('estadisticas.noActionsRecorded'))) {
        return;
      }
    }

    const finalScore = `${puntuacionLocal}-${puntuacionVisitante}`;

    // 1) Crear el partido
    api
      .post('/api/stats/games', {
        // Enviamos también el groupe_id
        groupe_id: groupeId,
        game_label: currentGame.game_label,
        adversary_name: currentGame.adversary_name,
        number_of_periods: currentGame.number_of_periods,
        period_duration: currentGame.period_duration,
        game_date: currentGame.game_date,
        game_location: currentGame.game_location,
        score: finalScore
      })
      .then((res) => {
        const newGameId = res.data.gameId;

        // 2) Construir la lista final (1 entrada par joueur)
        const allPlayerIds = new Set([
          ...Object.keys(playerTimeTracking).map((pid) => Number(pid)),
          ...actionsLog.map((a) => a.playerId)
        ]);

        const finalActions = Array.from(allPlayerIds).map((pid) => {
          const tracking = playerTimeTracking[pid] || {
            realTimeSegments: [],
            videoTimeSegments: []
          };
          const playerActions = actionsLog
            .filter((act) => act.playerId === pid)
            .map((act) => ({
              action: act.action,
              period: act.period,
              timeSec: act.timeSec
            }));

          return {
            playerId: pid,
            actions: playerActions,
            realTimeSegments: tracking.realTimeSegments,
            videoTimeSegments: tracking.videoTimeSegments
          };
        });

        // 3) Guardar en un solo POST
        api
          .post('/api/stats/actions', {
            game_id: newGameId,
            actions: finalActions
          })
          .then(() => {
            alert(t('estadisticas.gameSavedSuccess'));
          })
          .catch((err2) => {
            console.error('Error al guardar las acciones:', err2);
            alert(t('estadisticas.saveActionsError'));
          });
      })
      .catch((err) => {
        console.error('Error al crear el partido:', err);
        alert(t('estadisticas.createGameError'));
      });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    /**
     * IMPORTANT :
     * On utilise <DndProvider backend={MultiBackend} options={HTML5toTouch}> 
     * pour gérer à la fois le drag & drop souris (HTML5Backend) ET tactile (TouchBackend)
     */
    <DndProvider backend={MultiBackend} options={HTML5toTouch}>
      <Box sx={{ display: 'flex', width: '100%', height: '100vh', overflow: 'hidden' }}>
        {/* Columna izquierda: Banco + dropzone para jugadores activos que salen */}
        <DropZoneBanc
          onPlayerCheckOut={(player) => handlePlayerCheckOutZone(player)}
          isRunning={isRunning}
          t={t}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('estadisticas.playerBench')}
          </Typography>

          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpenConfigGame}
            sx={{ mb: 2 }}
          >
            {t('estadisticas.configureGame')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveGameAndActions}
            sx={{ mb: 2 }}
          >
            {t('estadisticas.saveGameActions')}
          </Button>

          <List>
            {players.map((player) => (
              <React.Fragment key={player.id}>
                <PlayerListItem player={player} onSwap={handleSwap} />
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </DropZoneBanc>

        {/* Parte central: Marcador + Campo */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {currentGame && (
            <Card sx={{ borderRadius: 2, m: 1, mb: 2 }}>
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                {/* Nombre del grupo (equipo local) */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {groupName}
                  </Typography>
                </Box>

                {/* Marcador local - visitante */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                    {puntuacionLocal} - {puntuacionVisitante}
                  </Typography>
                  <Typography variant="body2">
                    {t('estadisticas.period')}: {currentPeriod}/{currentGame.number_of_periods}
                  </Typography>
                </Box>

                {/* Nombre del adversario + botones para sumar puntos */}
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {currentGame.adversary_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onMouseDown={() => handleAdversaryScoreMouseDown(1)}
                      onMouseUp={() => handleAdversaryScoreMouseUp(1)}
                    >
                      1pt
                    </Button>
                    <Button
                      variant="contained"
                      onMouseDown={() => handleAdversaryScoreMouseDown(2)}
                      onMouseUp={() => handleAdversaryScoreMouseUp(2)}
                    >
                      2pts
                    </Button>
                    <Button
                      variant="contained"
                      onMouseDown={() => handleAdversaryScoreMouseDown(3)}
                      onMouseUp={() => handleAdversaryScoreMouseUp(3)}
                    >
                      3pts
                    </Button>
                  </Box>
                </Box>
              </CardContent>

              {/* Cronómetro del partido */}
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {formatTime(timeLeft)}
                  </Typography>
                  <Typography variant="caption">
                    {t('estadisticas.gameTimer')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button variant="contained" onClick={handleStartStopTimer}>
                    {isRunning ? t('estadisticas.stopGame') : t('estadisticas.startGame')}
                  </Button>
                  <Button variant="contained" color="info" onClick={handleEndOfPeriod}>
                    {t('estadisticas.endPeriod')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Campo + jugadores activos (columna derecha) */}
          <BasketballCourt
            activePlayers={activePlayers}
            setActivePlayers={setActivePlayers}
            setPlayers={setPlayers}
            onPlayerDropForShot={handlePlayerDropForShot}
            onOpenSpecialDialog={openDialogSpecialActions}
            onPlayerCheckIn={handlePlayerCheckIn}
            isRunning={isRunning}
            t={t}
          />
        </Box>
      </Box>

      {/* Capa de Drag personalizada */}
      <CustomDragLayer />

      {/* Snackbar (mensaje de cambio) */}
      <Snackbar
        open={!!exchangeMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setExchangeMessage(null)}
        autoHideDuration={3000}
      >
        <Alert onClose={() => setExchangeMessage(null)} severity="info" sx={{ width: '100%' }}>
          {exchangeMessage}
        </Alert>
      </Snackbar>

      {/* Dialog de tiro */}
      <Dialog open={openShotDialog} onClose={handleCloseShotDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontSize: '2rem', textAlign: 'center' }}>
          {t('estadisticas.shotSelection')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {shotPlayer && (
              <Avatar sx={{ width: 80, height: 80, mr: 2 }} src={shotPlayer.avatarUrl}>
                {shotPlayer.nom?.charAt(0).toUpperCase() || '?'}
              </Avatar>
            )}
            <Typography variant="h6">
              {t('estadisticas.player')}: {shotPlayer?.nom}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => handleShot('2pts', 'anotado')}
            >
              {t('estadisticas.twoPointsMade')}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={() => handleShot('2pts', 'fallado')}
            >
              {t('estadisticas.twoPointsMissed')}
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => handleShot('3pts', 'anotado')}
            >
              {t('estadisticas.threePointsMade')}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={() => handleShot('3pts', 'fallado')}
            >
              {t('estadisticas.threePointsMissed')}
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => handleShot('TL', 'anotado')}
            >
              {t('estadisticas.freeThrowMade')}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="large"
              onClick={() => handleShot('TL', 'fallado')}
            >
              {t('estadisticas.freeThrowMissed')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={handleCloseShotDialog} color="inherit" size="large">
            {t('estadisticas.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de acciones especiales (rebotes, falta, etc.) */}
      <Dialog open={openSpecialDialog} onClose={handleCloseSpecialDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontSize: '2rem', textAlign: 'center' }}>
          {t('estadisticas.specialActions')}
        </DialogTitle>
        <DialogContent>
          {specialPlayer && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ width: 80, height: 80, mr: 2 }} src={specialPlayer.avatarUrl}>
                {specialPlayer.nom?.charAt(0).toUpperCase() || '?'}
              </Avatar>
              <Typography variant="h6">
                {specialPlayer.nom}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSpecialAction(t('estadisticas.offensiveRebound'))}
            >
              {t('estadisticas.offensiveRebound')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSpecialAction(t('estadisticas.defensiveRebound'))}
            >
              {t('estadisticas.defensiveRebound')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSpecialAction(t('estadisticas.steal'))}
            >
              {t('estadisticas.steal')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSpecialAction(t('estadisticas.turnover'))}
            >
              {t('estadisticas.turnover')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSpecialAction(t('estadisticas.block'))}
            >
              {t('estadisticas.block')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSpecialAction(t('estadisticas.foul'))}
            >
              {t('estadisticas.foul')}
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handleSpecialAction(t('estadisticas.assist'))}
            >
              {t('estadisticas.assist')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={handleCloseSpecialDialog} color="inherit" size="large">
            {t('estadisticas.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de configuración de partido */}
      <Dialog open={openConfigGameDialog} onClose={handleCloseConfigGame} fullWidth maxWidth="sm">
        <DialogTitle>{t('estadisticas.configureGameTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('estadisticas.gameName')}
            fullWidth
            value={unpublishedGame.game_label}
            onChange={(e) =>
              setUnpublishedGame({ ...unpublishedGame, game_label: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('estadisticas.adversaryName')}
            fullWidth
            value={unpublishedGame.adversary_name}
            onChange={(e) =>
              setUnpublishedGame({ ...unpublishedGame, adversary_name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('estadisticas.numberOfPeriods')}
            type="number"
            fullWidth
            value={unpublishedGame.number_of_periods}
            onChange={(e) =>
              setUnpublishedGame({ ...unpublishedGame, number_of_periods: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('estadisticas.periodDuration')}
            type="number"
            fullWidth
            value={unpublishedGame.period_duration}
            onChange={(e) =>
              setUnpublishedGame({ ...unpublishedGame, period_duration: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label={t('estadisticas.gameDate')}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={unpublishedGame.game_date}
            onChange={(e) =>
              setUnpublishedGame({ ...unpublishedGame, game_date: e.target.value })
            }
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('estadisticas.location')}</InputLabel>
            <Select
              label={t('estadisticas.location')}
              value={unpublishedGame.game_location}
              onChange={(e) =>
                setUnpublishedGame({ ...unpublishedGame, game_location: e.target.value })
              }
            >
              <MenuItem value="domicile">{t('estadisticas.home')}</MenuItem>
              <MenuItem value="exterieur">{t('estadisticas.away')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfigGame} color="inherit">
            {t('estadisticas.cancel')}
          </Button>
          <Button onClick={handleConfigGame} variant="contained" color="primary">
            {t('estadisticas.validate')}
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
}
